import { useEffect, useState } from "react";
import { adminApi } from "@/admin/api";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { IndianRupee, TrendingUp, Calendar, CheckCircle2, XCircle, Clock, Users, BarChart3 } from "lucide-react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const Stat = ({ icon: Icon, label, value, tint }) => (
  <div data-testid={`stat-${label}`} className="bg-white rounded-xl border border-gray-200 p-5">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${tint}`}><Icon size={18} /></div>
    <div className="text-2xl font-bold text-gray-900">{value}</div>
    <div className="text-xs text-gray-500 mt-0.5">{label}</div>
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get("/admin/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-gray-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-gray-900">Overview</h1>
        <div className="flex items-center gap-2 text-xs">
          <span data-testid="dash-mode" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-bold uppercase ${d.mode === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{d.mode} mode</span>
          {d.test_records > 0 && <span className="text-gray-400">{d.test_records} test record(s) hidden from public &amp; these stats</span>}
        </div>
      </div>
      <p className="text-xs text-gray-400 -mt-3">Figures reflect <strong>live-only</strong> data. Test-mode donations never appear on the public site or in these totals.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={IndianRupee} label="Total Raised" value={inr(d.total_donations)} tint="bg-emerald-100 text-emerald-700" />
        <Stat icon={Calendar} label="Today" value={inr(d.today_donations)} tint="bg-blue-100 text-blue-700" />
        <Stat icon={TrendingUp} label="This Month" value={inr(d.month_donations)} tint="bg-orange-100 text-orange-700" />
        <Stat icon={Users} label="Total Donors" value={d.total_donors} tint="bg-violet-100 text-violet-700" />
        <Stat icon={CheckCircle2} label="Successful" value={d.successful} tint="bg-emerald-100 text-emerald-700" />
        <Stat icon={XCircle} label="Failed" value={d.failed} tint="bg-red-100 text-red-700" />
        <Stat icon={Clock} label="Pending" value={d.pending} tint="bg-amber-100 text-amber-700" />
        <Stat icon={BarChart3} label="Avg Donation" value={inr(d.avg_donation)} tint="bg-teal-100 text-teal-700" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="font-semibold text-gray-800 mb-4">Donation Trends</h2>
        <div className="h-64" data-testid="trends-chart">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <AreaChart data={d.trends}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#c85a30" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#c85a30" stopOpacity={0} />
              </linearGradient></defs>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => inr(v)} />
              <Area type="monotone" dataKey="amount" stroke="#c85a30" fill="url(#g)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-4">Recent Donations</h2>
          <div className="space-y-3" data-testid="recent-donations">
            {d.recent.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
                <div><div className="font-medium text-gray-800">{r.name}</div><div className="text-xs text-gray-400">{r.cause}</div></div>
                <div className="text-right"><div className="font-semibold text-[hsl(13_65%_44%)]">{inr(r.amount)}</div><div className="text-[10px] text-gray-400">{r.status}</div></div>
              </div>
            ))}
            {d.recent.length === 0 && <p className="text-sm text-gray-400">No donations yet.</p>}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-800 mb-4">Recent Admin Activity</h2>
          <div className="space-y-3" data-testid="recent-activity">
            {d.activity.map((a) => (
              <div key={a.id} className="text-sm border-b border-gray-100 pb-2">
                <span className="font-medium text-gray-800">{a.admin_email}</span>
                <span className="text-gray-500"> — {a.action.replace(/_/g, " ")}</span>
                {a.detail ? <span className="text-gray-400"> · {a.detail}</span> : null}
                <div className="text-[10px] text-gray-400">{new Date(a.created_at).toLocaleString("en-IN")}</div>
              </div>
            ))}
            {d.activity.length === 0 && <p className="text-sm text-gray-400">No activity yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
